import { useState } from 'react'

import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import type { AdminUser } from '@/features/admin/api/users.api'
import { useAdminUsersQuery } from '@/features/admin/hooks/useAdminUsers'
import { formatDate, humanizeEnum } from '@/utils/format'

const USER_TYPE_TABS = [
  { key: '', label: 'All' },
  { key: 'student', label: 'Students' },
  { key: 'recruiter_admin', label: 'Recruiter Admin' },
  { key: 'recruiter_user', label: 'Recruiter User' },
  { key: 'super_admin', label: 'Super Admin' },
]

const TYPE_BADGE: Record<string, string> = {
  student:          'bg-info-transparent text-info',
  recruiter_admin:  'bg-primary-transparent text-primary',
  recruiter_user:   'bg-secondary-transparent text-secondary',
  super_admin:      'bg-danger-transparent text-danger',
}

const STATUS_BADGE: Record<string, string> = {
  active:               'bg-success-transparent text-success',
  inactive:             'bg-secondary-transparent text-secondary',
  blocked:              'bg-danger-transparent text-danger',
  pending_verification: 'bg-warning-transparent text-warning',
}

const COLUMNS: DataTableColumn<AdminUser>[] = [
  {
    key: 'name',
    header: 'Name',
    render: (row) => (
      <div className="d-flex align-items-center gap-2">
        <span
          className="avatar avatar-sm avatar-rounded bg-primary-transparent fs-12 fw-semibold text-primary"
          style={{ minWidth: 30, height: 30 }}
        >
          {row.name.charAt(0).toUpperCase()}
        </span>
        <span className="fs-13 fw-medium">{row.name}</span>
      </div>
    ),
  },
  {
    key: 'email',
    header: 'Email',
    render: (row) => <span className="fs-13 text-muted">{row.email}</span>,
  },
  {
    key: 'userType',
    header: 'Type',
    render: (row) => (
      <span className={`badge fs-11 ${TYPE_BADGE[row.userType] ?? 'bg-secondary-transparent'}`}>
        {humanizeEnum(row.userType)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => (
      <span className={`badge fs-11 ${STATUS_BADGE[row.status] ?? 'bg-secondary-transparent'}`}>
        {humanizeEnum(row.status)}
      </span>
    ),
  },
  {
    key: 'organizationName',
    header: 'Organisation',
    render: (row) => (
      <span className="fs-13 text-muted">{row.organizationName ?? '—'}</span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Joined',
    render: (row) => <span className="fs-12 text-muted">{row.createdAt ? formatDate(row.createdAt) : '—'}</span>,
  },
]

export function AdminUsersPage() {
  const [userType, setUserType] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)

  const { data, isPending } = useAdminUsersQuery({
    user_type: userType || undefined,
    q: q || undefined,
    page,
    per_page: 10,
  })

  const pagination = data?.pagination

  const handleTabChange = (type: string) => {
    setUserType(type)
    setPage(1)
  }

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <div>
          <p className="fw-semibold fs-20 mb-1">All Users</p>
          <p className="text-muted fs-13 mb-0">
            {pagination ? `${pagination.total} users registered` : 'All registered users across the platform.'}
          </p>
        </div>
        {/* Search */}
        <div style={{ minWidth: 240 }}>
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-white">
              <i className="ti ti-search fs-13 text-muted" />
            </span>
            <input
              type="text"
              className="form-control fs-13"
              placeholder="Search name or email…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1) }}
            />
          </div>
        </div>
      </div>

      {/* Type filter tabs */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {USER_TYPE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTabChange(tab.key)}
            className={`btn btn-sm ${userType === tab.key ? 'btn-primary' : 'btn-light'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card custom-card">
        <DataTable
          columns={COLUMNS}
          rows={data?.users ?? []}
          rowKey={(row) => row.id}
          loading={isPending}
          emptyMessage="No users found."
          pagination={pagination}
          onPageChange={(p) => setPage(p)}
        />
      </div>
    </>
  )
}
